import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  AppBar,
  Backdrop,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'

import { activateAccount } from 'src/redux/apiCalls/authApiCall'
import { getMinInstitutionInfo } from 'src/redux/slices/tableSlice/institutionSlice'

const ActivateAccount = () => {
  const { id, token } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const recaptchaRef = useRef(null)

  const { minInstitutionData, statusMin, error } = useSelector((state) => state.institution)

  const [connexion, setConnexion] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: '',
  })

  const langIsFr = minInstitutionData?.LANGUE_DEFAUT_ETAB === 'Français'

  const messages = useMemo(
    () => ({
      invalidPassword: langIsFr
        ? 'Le mot de passe doit respecter des exigences de complexité'
        : 'The password must comply with complexity requirements',
      passwordMismatch: langIsFr
        ? 'Le mot de passe et sa confirmation ne sont pas identiques'
        : 'The password and its confirmation are not identical',
      captchaRequired: langIsFr ? 'Veuillez valider le captcha' : 'Please complete the captcha',
      captchaExpired: langIsFr
        ? 'Le captcha a expiré, veuillez réessayer'
        : 'Captcha expired, please try again',
      pageTitle: langIsFr
        ? 'Portail des enseignants - Activer votre compte'
        : 'Teachers portal - Activate your account',
      email: langIsFr ? 'Email' : 'Mail',
      newPassword: langIsFr ? 'Nouveau mot de passe' : 'New password',
      confirmPassword: langIsFr ? 'Confirmer le nouveau mot de passe' : 'Confirm the new password',
      update: langIsFr ? 'Modifier' : 'Update',
      auth: langIsFr ? "S'authentifier" : 'Authenticate',
      home: langIsFr ? 'Accueil' : 'Home',
      dbConnecting: langIsFr
        ? 'Connexion à la base de données en cours...'
        : 'Connection to the current database...',
      dbFailed: langIsFr
        ? 'Connexion impossible ! Veuillez réessayer...'
        : 'Connection impossible! please try again...',
    }),
    [langIsFr],
  )

  const [formValues, setFormValues] = useState({
    newPassword: {
      error: true,
      errorMessage: messages.invalidPassword,
    },
    confirmPassword: {
      error: true,
      errorMessage: messages.passwordMismatch,
    },
  })

  const validatePassword = (password) => {
    const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
    return regex.test(password)
  }

  useEffect(() => {
    const newPasswordValid =
      passwords.newPassword.trim() === '' || validatePassword(passwords.newPassword)

    const confirmPasswordValid =
      passwords.confirmPassword.trim() === '' || passwords.newPassword === passwords.confirmPassword

    setFormValues({
      newPassword: {
        error: !newPasswordValid,
        errorMessage: messages.invalidPassword,
      },
      confirmPassword: {
        error: !confirmPasswordValid,
        errorMessage: messages.passwordMismatch,
      },
    })
  }, [passwords, messages])

  useEffect(() => {
    if (statusMin === 'idle') {
      dispatch(getMinInstitutionInfo())
    }

    setConnexion(statusMin === 'succeeded')
  }, [dispatch, statusMin])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setPasswords((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCaptchaExpired = () => {
    toast.warning(messages.captchaExpired)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isSubmitting) return

    if (
      formValues.newPassword.error ||
      formValues.confirmPassword.error ||
      !passwords.newPassword.trim() ||
      !passwords.confirmPassword.trim()
    ) {
      return
    }

    const captchaInstance = recaptchaRef.current
    const tokenCaptcha = captchaInstance?.getValue()

    if (!tokenCaptcha) {
      toast.error(messages.captchaRequired)
      return
    }

    try {
      setIsSubmitting(true)

      const result = await dispatch(
        activateAccount({
          MAIL_PRO_ENS: id,
          password: passwords.newPassword,
          token,
          recaptchaToken: tokenCaptcha,
          lang: minInstitutionData?.LANGUE_DEFAUT_ETAB,
        }),
      )

      if (
        result === 200 ||
        result?.payload === 200 ||
        result?.meta?.requestStatus === 'fulfilled'
      ) {
        navigate('/')
      }
    } catch (error) {
      toast.error(langIsFr ? "Erreur lors de l'activation du compte" : 'Account activation failed')
    } finally {
      if (captchaInstance) {
        captchaInstance.reset()
      }
      setIsSubmitting(false)
    }
  }

  const institutionLabel = !connexion
    ? error
      ? messages.dbFailed
      : messages.dbConnecting
    : langIsFr
    ? minInstitutionData?.LIB_ETAB?.length > 90
      ? minInstitutionData?.LIB_ETAB_C
      : minInstitutionData?.LIB_ETAB
    : minInstitutionData?.LIB_ETAB_EN?.length > 90
    ? minInstitutionData?.LIB_ETAB_EN_C
    : minInstitutionData?.LIB_ETAB_EN

  const institutionLabelSmall = langIsFr
    ? minInstitutionData?.LIB_ETAB_C
    : minInstitutionData?.LIB_ETAB_EN_C

  const isSubmitDisabled =
    isSubmitting ||
    formValues.newPassword.error ||
    formValues.confirmPassword.error ||
    passwords.newPassword.trim() === '' ||
    passwords.confirmPassword.trim() === ''

  const Copyright = () => (
    <Grid sx={{ p: 2 }}>
      <Typography variant="body2" color="textSecondary" align="center">
        {'Copyright © '}
        <Link
          href="https://universco.fcsdz.com/"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          UNIVERSCO Web App 2024
        </Link>
      </Typography>
    </Grid>
  )

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={!connexion || statusMin === 'idle' || statusMin === 'failed'}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ flexGrow: 1 }}>
        <AppBar sx={{ background: '#000000' }} position="static">
          <Toolbar>
            <CardMedia
              component="img"
              sx={{ width: 200, height: 60 }}
              image="/logo_universco.png"
              alt="Logo"
            />

            <Typography
              sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block', xl: 'none' }, ml: 2 }}
              variant="h5"
              component="div"
            >
              {institutionLabel}
            </Typography>

            <Typography
              sx={{ flexGrow: 1, display: { xs: 'block', lg: 'none', xl: 'block' }, ml: 2 }}
              variant="h5"
              component="div"
            >
              {institutionLabelSmall}
            </Typography>

            <Button
              color="inherit"
              component={RouterLink}
              to={window.location.origin.replace('teachers-', '')}
            >
              {messages.home}
            </Button>
          </Toolbar>
        </AppBar>
      </Box>

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ m: 1 }}
      >
        <Grid item>
          <CardMedia
            component="img"
            sx={{ height: 80 }}
            image={`${process.env.REACT_APP_API_URL}/images/logo_institution.png`}
            alt="Logo"
          />
        </Grid>
      </Grid>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: '500px',
          margin: 'auto',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          backgroundColor: 'white',
          marginTop: 2,
        }}
      >
        <Typography variant="h5" component="div" sx={{ mb: 2 }}>
          {messages.pageTitle}
        </Typography>

        <Grid container spacing={2} mb={2}>
          <Grid item md={12} xs={12}>
            <TextField
              autoComplete="off"
              fullWidth
              value={id || ''}
              label={messages.email}
              margin="normal"
              variant="standard"
              name="username"
              disabled
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item md={12} xs={12}>
            <TextField
              label={messages.newPassword}
              name="newPassword"
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={passwords.newPassword}
              variant="standard"
              InputLabelProps={{ shrink: true }}
              onChange={handleInputChange}
              error={formValues.newPassword.error}
              helperText={formValues.newPassword.error ? formValues.newPassword.errorMessage : ''}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={2}>
          <Grid item md={12} xs={12}>
            <TextField
              label={messages.confirmPassword}
              name="confirmPassword"
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={passwords.confirmPassword}
              variant="standard"
              InputLabelProps={{ shrink: true }}
              onChange={handleInputChange}
              error={formValues.confirmPassword.error}
              helperText={
                formValues.confirmPassword.error ? formValues.confirmPassword.errorMessage : ''
              }
            />
          </Grid>
        </Grid>

        {langIsFr ? (
          <>
            <Typography variant="body2">Votre mot de passe doit contenir :</Typography>
            <Typography variant="body2">- au minimum 8 caractères,</Typography>
            <Typography variant="body2">- au moins une lettre minuscule,</Typography>
            <Typography variant="body2">- au moins une lettre majuscule,</Typography>
            <Typography variant="body2">- au moins un chiffre,</Typography>
            <Typography variant="body2">- au moins un caractère spécial.</Typography>
          </>
        ) : (
          <>
            <Typography variant="body2">Your password must contain :</Typography>
            <Typography variant="body2">- at least 8 characters,</Typography>
            <Typography variant="body2">- at least one lowercase letter,</Typography>
            <Typography variant="body2">- at least one uppercase letter,</Typography>
            <Typography variant="body2">- at least one digit,</Typography>
            <Typography variant="body2">- at least one special character.</Typography>
          </>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onExpired={handleCaptchaExpired}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2, background: '#000000' }}
          disabled={isSubmitDisabled}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : messages.update}
        </Button>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" underline="hover">
            {messages.auth}
          </Link>
        </Box>
      </Box>

      <Copyright />
    </>
  )
}

export default ActivateAccount
